<script>
    export let label = "Click me";
    export let link = "#"; // The actual Google Meet link
    export let classId = ""; // Add class ID as a prop

    // State to control showing the manual link
    let showManualLink = false;
    let linkCopied = false;

    // Function to handle navigation with attendance tracking
    async function handleClassJoin(event) {
        // Prevent default navigation
        event.preventDefault();
        
        // CRITICAL: Open window immediately to avoid popup blockers
        // We'll update the location after fetching the link
        const newWindow = window.open('about:blank', '_blank');
        
        // If popup was blocked, newWindow will be null
        if (!newWindow) {
            console.error('Popup was blocked by browser. Using fallback link.');
            // Fallback: navigate in same tab
            window.location.href = link;
            return;
        }
        
        try {
            // Get JWT token from localStorage
            const token = localStorage.getItem('jwtToken');
            
            // Track attendance first
            const response = await fetch(`/studentplayground/track-attendance/${classId}/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Attendance tracking failed:', errorData);
                throw new Error(`Failed to track attendance: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            if (data && data.redirect_url) {
                // Log the redirect URL for debugging
                console.log('Opening redirect URL:', data.redirect_url);
                // Update the already-opened window's location
                newWindow.location.href = data.redirect_url;
            } else {
                console.error('No redirect URL in response:', data);
                console.log('Falling back to original link:', link);
                // Fallback to the original link
                newWindow.location.href = link;
            }
        } catch (error) {
            console.error('Error tracking attendance:', error);
            console.log('Fallback: Opening original link due to error:', link);
            // If tracking fails, still allow them to join the class
            newWindow.location.href = link;
        }
    }

    // Function to toggle showing the manual link
    function toggleManualLink() {
        showManualLink = !showManualLink;
    }

    // Function to copy link to clipboard
    async function copyToClipboard() {
        try {
            await navigator.clipboard.writeText(link);
            linkCopied = true;
            // Reset after 2 seconds
            setTimeout(() => {
                linkCopied = false;
            }, 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    }
</script>

<div class="manual-link-container">
    <div class="help-text" 
         on:click={toggleManualLink} 
         on:keypress={(e) => e.key === 'Enter' && toggleManualLink()}
         role="button"
         tabindex="0">
        {showManualLink ? 'Nukopijuotą nuorodą įspausdinkite į naršyklės paieškos langelį' : 'Nepavyksta prisijungti prie pamokos?'}
    </div>
    
    {#if showManualLink}
        <div class="link-display">
            <a href={link} target="_blank" rel="noopener noreferrer" class="meeting-link" on:click={handleClassJoin}>
                {link}
            </a>
            <button class="copy-btn" on:click={copyToClipboard}>
                {linkCopied ? 'Nuoroda nukopijuota' : 'Kopijuoti nuorodą'}
            </button>
        </div>
    {/if}
</div>

<button on:click={handleClassJoin}>
    <span>{label}</span>
</button>

<style>
    /* Button styling */
    button span {
        font-weight: bolder;
        font-size:larger;
    }

    button {
        box-sizing: border-box;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 12px 20px;
        gap: 10px;
    
        width: 100%;
        height: 60px;
    
        /* Purple background */
        background-color: #faad4a;
    
        /* Black border */
        border: 2px solid #000000;
    
        /* Box shadow */
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    
        /* Rounded corners */
        border-radius: 8px;
    
        /* Inside auto layout */
        flex: none;
        order: 0;
        align-self: stretch;
        flex-grow: 0;
    
        /* Remove default button styling */
        outline: none;
        cursor: pointer;
  
        /* Remove the default button background on focus */
        background-image: none;
        transition: background-color 0.3s ease, color 0.3s ease;
    }

    /* Button hover effect */
    button:hover {
        background-color: #14695b;
        color:white;
    }

    /* Text inside the button */
    span {
        font-size: 1rem;
        font-weight: bold;
        
    }

    /* Manual link container */
    .manual-link-container {
        margin-bottom: 4px;
        width: 100%;
    }

    /* Help text styling */
    .help-text {
        text-align: center;
        color: #666;
        font-size: 0.75rem;
        cursor: pointer;
        margin: 0;
        text-decoration: underline;
        transition: color 0.2s ease;
    }

    .help-text:hover {
        color: #14695b;
    }

    /* Link display container */
    .link-display {
        margin-top: 8px;
        margin-bottom: 8px;
        padding: 12px;
        background-color: #f5f5f5;
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    /* Meeting link styling */
    .meeting-link {
        color: #14695b;
        word-break: break-all;
        font-size: 0.9rem;
        text-decoration: none;
        padding: 8px;
        background-color: white;
        border-radius: 4px;
        border: 1px solid #ddd;
    }

    .meeting-link:hover {
        text-decoration: underline;
        background-color: #f9f9f9;
    }

    /* Copy button styling */
    .copy-btn {
        padding: 8px 16px;
        background-color: #14695b;
        color: white;
        border: 1px solid #14695b;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: bold;
        transition: background-color 0.2s ease;
        height: auto;
    }

    .copy-btn:hover {
        background-color: #0f5347;
    }
</style>
